import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
// Import Layout directly to avoid barrel export triggering store initialization
import { Layout } from '@/components/Layout/Layout';
import { ChunkLoadErrorBoundary } from '@/components/ChunkLoadErrorBoundary';
import {
  SubscriptionRoute,
  TeacherOnlyRoute,
  StudentOnlyRoute,
  AdminOnlyRoute,
} from './ProtectedRoute';

// ============================================
// PLATFORM ROUTES
// ============================================

// Landing page - Marketing page for new visitors
const LandingPage = lazy(() =>
  import('@/pages/LandingPage').then((m) => ({
    default: m.LandingPage,
  }))
);

// Main login page with teacher email/password and student animal password
const SimplifiedLoginPage = lazy(() =>
  import('@/pages/SimplifiedLoginPage').then((m) => ({
    default: m.SimplifiedLoginPage,
  }))
);

// Workspace page - Teacher homepage with app grid
const WorkspacePage = lazy(() =>
  import('@/pages/WorkspacePage').then((m) => ({ default: m.WorkspacePage }))
);

// Dynamic app page - Renders any registered app
const AppPage = lazy(() =>
  import('@/pages/AppPage').then((m) => ({ default: m.AppPage }))
);

// Slides page - Dedicated slides management page
const SlidesPage = lazy(() =>
  import('@/pages/SlidesPage').then((m) => ({ default: m.SlidesPage }))
);

// Canvas page - Customizable widget canvas
const CanvasPage = lazy(() =>
  import('@/pages/CanvasPage').then((m) => ({ default: m.CanvasPage }))
);

// Legacy whiteboard page - WB-01 only (kept for backward compatibility)
const WhiteboardPage = lazy(() =>
  import('@/pages/WhiteboardPage').then((m) => ({ default: m.WhiteboardPage }))
);

// Teacher-only page for animal code management
const TeacherAnimalCodePage = lazy(() =>
  import('@/pages/TeacherAnimalCodePage').then((m) => ({
    default: m.TeacherAnimalCodePage,
  }))
);

// Admin page (admin-only)
const AdminPage = lazy(() =>
  import('@/pages/AdminPage').then((m) => ({
    default: m.AdminPage,
  }))
);

// Student cast page - Shows cast app or waiting screen
const StudentCastPage = lazy(() =>
  import('@/pages/StudentCastPage').then((m) => ({ default: m.StudentCastPage }))
);

// Interactive Manipulatives page (teacher-only)
const ManipulativesPage = lazy(() =>
  import('@/pages/ManipulativesPage').then((m) => ({
    default: m.ManipulativesPage,
  }))
);

// Classroom Management page (teacher-only)
const ClassroomPage = lazy(() =>
  import('@/pages/ClassroomPage').then((m) => ({
    default: m.ClassroomPage,
  }))
);

// 404 page
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);

// ============================================
// HIDDEN ROUTES (kept for future use)
// Uncomment these when ready to restore functionality
// ============================================

/*
const HomePage = lazy(() =>
  import('@/pages/HomePage').then((m) => ({ default: m.HomePage }))
);
const LoginPage = lazy(() =>
  import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const SignupPage = lazy(() =>
  import('@/pages/SignupPage').then((m) => ({ default: m.SignupPage }))
);
const ForgotPasswordPage = lazy(() =>
  import('@/pages/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  }))
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  }))
);
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const StudentListPage = lazy(() =>
  import('@/pages/StudentListPage').then((m) => ({ default: m.StudentListPage }))
);
const WordBankListPage = lazy(() =>
  import('@/pages/WordBankListPage').then((m) => ({
    default: m.WordBankListPage,
  }))
);
const WhiteboardSelectionPage = lazy(() =>
  import('@/pages/WhiteboardSelectionPage').then((m) => ({
    default: m.WhiteboardSelectionPage,
  }))
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
*/

/**
 * Loading fallback component for lazy-loaded routes.
 * Minimal to avoid jank and ensure fast perceived load.
 */
function PageLoading() {
  return (
    <div
      className="flex h-full min-h-[200px] items-center justify-center"
      role="status"
      aria-label="Loading page"
    >
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
    </div>
  );
}

/**
 * Wraps a lazy-loaded component with Suspense boundary
 */
function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

// Dev-only routes (only included in development builds)
const devRoutes = import.meta.env.DEV
  ? [
      // Dev whiteboard - bypasses auth for local testing
      {
        path: 'dev/whiteboard',
        element: (
          <LazyPage>
            <WhiteboardPage />
          </LazyPage>
        ),
      },
      // Dev canvas - bypasses auth for local testing
      {
        path: 'dev/canvas',
        element: (
          <LazyPage>
            <CanvasPage />
          </LazyPage>
        ),
      },
    ]
  : [];

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <ChunkLoadErrorBoundary />,
    children: [
      // ============================================
      // DEV-ONLY ROUTES (only in development)
      // ============================================
      ...devRoutes,

      // ============================================
      // PLATFORM ROUTES
      // ============================================

      // Landing page - Marketing page for new visitors
      {
        index: true,
        element: (
          <LazyPage>
            <LandingPage />
          </LazyPage>
        ),
      },

      // Login page - Teacher and student authentication
      {
        path: 'login',
        element: (
          <LazyPage>
            <SimplifiedLoginPage />
          </LazyPage>
        ),
      },

      // Workspace - Teacher homepage with app grid (teacher-only)
      {
        path: 'workspace',
        element: (
          <TeacherOnlyRoute>
            <SubscriptionRoute>
              <LazyPage>
                <WorkspacePage />
              </LazyPage>
            </SubscriptionRoute>
          </TeacherOnlyRoute>
        ),
      },

      // Dynamic app page - Renders any registered app by ID
      {
        path: 'app/:appId',
        element: (
          <SubscriptionRoute>
            <LazyPage>
              <AppPage />
            </LazyPage>
          </SubscriptionRoute>
        ),
      },

      // Slides management page (teacher-only)
      {
        path: 'slides',
        element: (
          <TeacherOnlyRoute>
            <SubscriptionRoute>
              <LazyPage>
                <SlidesPage />
              </LazyPage>
            </SubscriptionRoute>
          </TeacherOnlyRoute>
        ),
      },

      // Widget canvas page - Customizable classroom screen (teacher-only)
      {
        path: 'canvas',
        element: (
          <TeacherOnlyRoute>
            <SubscriptionRoute>
              <LazyPage>
                <CanvasPage />
              </LazyPage>
            </SubscriptionRoute>
          </TeacherOnlyRoute>
        ),
      },

      // Interactive Manipulatives - Sub-page listing manipulative apps (teacher-only)
      {
        path: 'manipulatives',
        element: (
          <TeacherOnlyRoute>
            <SubscriptionRoute>
              <LazyPage>
                <ManipulativesPage />
              </LazyPage>
            </SubscriptionRoute>
          </TeacherOnlyRoute>
        ),
      },

      // Classroom Management - Sub-page listing management tools (teacher-only)
      {
        path: 'classroom-management',
        element: (
          <TeacherOnlyRoute>
            <SubscriptionRoute>
              <LazyPage>
                <ClassroomPage />
              </LazyPage>
            </SubscriptionRoute>
          </TeacherOnlyRoute>
        ),
      },

      // Legacy whiteboard route - Redirects to Word Arc app
      {
        path: 'whiteboard',
        element: <Navigate to="/app/word-arc" replace />,
      },

      // Admin page - manage admin accounts (admin-only)
      {
        path: 'admin',
        element: (
          <AdminOnlyRoute>
            <LazyPage>
              <AdminPage />
            </LazyPage>
          </AdminOnlyRoute>
        ),
      },

      // Teacher-only: Animal code management page
      {
        path: 'teacher/code',
        element: (
          <TeacherOnlyRoute>
            <LazyPage>
              <TeacherAnimalCodePage />
            </LazyPage>
          </TeacherOnlyRoute>
        ),
      },

      // Student-only: Cast view page (shows cast app or waiting screen)
      {
        path: 'student',
        element: (
          <StudentOnlyRoute>
            <LazyPage>
              <StudentCastPage />
            </LazyPage>
          </StudentOnlyRoute>
        ),
      },

      // ============================================
      // HIDDEN ROUTES (commented out for MVP)
      // ============================================

      /*
      // Original home page
      {
        path: 'home',
        element: (
          <LazyPage>
            <HomePage />
          </LazyPage>
        ),
      },
      // Original login page
      {
        path: 'login',
        element: (
          <LazyPage>
            <LoginPage />
          </LazyPage>
        ),
      },
      // Signup
      {
        path: 'signup',
        element: (
          <LazyPage>
            <SignupPage />
          </LazyPage>
        ),
      },
      // Password reset flow
      {
        path: 'forgot-password',
        element: (
          <LazyPage>
            <ForgotPasswordPage />
          </LazyPage>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <LazyPage>
            <ResetPasswordPage />
          </LazyPage>
        ),
      },
      // Dashboard
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <LazyPage>
              <DashboardPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      // Student management
      {
        path: 'dashboard/students',
        element: (
          <ProtectedRoute>
            <LazyPage>
              <StudentListPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      // Word bank management
      {
        path: 'dashboard/wordbanks',
        element: (
          <ProtectedRoute>
            <LazyPage>
              <WordBankListPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      // Whiteboard selection (WB-01 vs WB-02)
      {
        path: 'dashboard/whiteboards',
        element: (
          <ProtectedRoute>
            <LazyPage>
              <WhiteboardSelectionPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      // Settings
      {
        path: 'dashboard/settings',
        element: (
          <ProtectedRoute>
            <LazyPage>
              <SettingsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      // Dynamic whiteboard with ID (WB-01, WB-02, etc.)
      {
        path: 'whiteboard/:id',
        element: (
          <ProtectedRoute>
            <LazyPage>
              <WhiteboardPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      */

      // 404 Not Found
      {
        path: '*',
        element: (
          <LazyPage>
            <NotFoundPage />
          </LazyPage>
        ),
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}

export {
  ProtectedRoute,
  SubscriptionRoute,
  TeacherOnlyRoute,
  StudentOnlyRoute,
  AdminOnlyRoute,
} from './ProtectedRoute';
